/**
 * Fetch Dynamic Configuration
 *
 * Fetches dynamic configuration from CDN (feature flags, availability, etc.)
 * Based on V1's loadDynamicConfiguration.js
 */

import type { DynamicConfiguration } from '@/domain/configuration';
import { AVAILABILITY } from '@/domain/configuration';
import { validDynamicConfiguration } from '@/domain/validators/validDynamicConfiguration';
import { logger } from '@/lib/logger';
import sofetch from '@/lib/sofetch';

export default async function fetchDynamicConfiguration(cdn: string, appId: string): Promise<DynamicConfiguration> {
  logger.log('Fetching dynamic configuration...');

  try {
    const url = `${cdn}/orgs/configs/chat/${appId}-dynamic.json?q=${new Date().getTime()}`;
    // cache: 'no-cache' allows caches to store a response but requires them to
    // revalidate it before reuse. The timestamp query param is a belt-and-suspenders
    // cache-buster for CDNs and browsers that ignore the cache directive.
    const response = await sofetch.get(url, {
      cache: 'no-cache',
    });

    if (!response.data) {
      logger.log('No response for dynamic configuration, returning default configuration');
      return getDefaultDynamicConfiguration();
    }

    // Validate the dynamic configuration data
    const dynamicConfiguration = validDynamicConfiguration(response.data);
    logger.log('Dynamic configuration fetched', dynamicConfiguration);
    return dynamicConfiguration;
  } catch (error) {
    const status = error instanceof Response ? error.status : 'unknown';
    logger.error(`Failed to fetch dynamic configuration for appID: ${appId}, (${status})`);
    return getDefaultDynamicConfiguration();
  }
}

function getDefaultDynamicConfiguration(): DynamicConfiguration {
  return {
    enabled: true,
    availability: AVAILABILITY.BUSINESS_CLOSED,
    percentageRollout: 0,
    featureFlags: {},
  };
}
